import { DataSourceTableId } from "@hex/common";
import { Placement } from "@popperjs/core";
import React, { useCallback } from "react";

import { useDataBrowserDialog } from "../../../data/data-browser-modal/useDataBrowserDialog";
import { useMentionTableSummaryByIdQuery } from "../../../rich-text/elementRenderers.generated";
import { TableMentionDetails } from "../../../rich-text/plugins/MentionDetails";
import { RichTextMentionData } from "../../../rich-text/plugins/RichTextMentionPlugin";

import { MentionTooltip } from "./MentionTooltip";

interface TableMentionPopoverProps {
  tableId: DataSourceTableId;
  children: React.ReactNode;
  placement?: Placement;
  maxWidthOverride?: number;
  openDataBrowserOverride?: (mention: RichTextMentionData.Table) => void;
}

export const TableMentionPopover = React.memo(function TableMentionPopover({
  children,
  maxWidthOverride,
  openDataBrowserOverride,
  placement,
  tableId,
}: TableMentionPopoverProps) {
  const { data, error } = useMentionTableSummaryByIdQuery({
    variables: {
      dataSourceTableId: tableId,
    },
  });

  const { openTableInDataBrowser } = useDataBrowserDialog({
    source: "LOGIC_VIEW_PROMPT_BAR",
  });

  const table = data?.dataSourceTableById;

  const showInDataBrowser = useCallback(
    (mention: RichTextMentionData.Table) => {
      openTableInDataBrowser({
        tableId: mention.id,
        dataConnectionId: mention.connectionId,
      });
    },
    [openTableInDataBrowser],
  );

  const tooltip = table && !error && (
    <TableMentionDetails
      mention={{
        tableName: table.name,
        schemaName: table.dataSourceSchema.name,
        databaseName: table.dataSourceSchema.dataSourceDatabase.name,
        mentionType: "table",
        id: table.id,
        connectionId:
          table.dataSourceSchema.dataSourceDatabase.dataConnectionId,
        description:
          table.metadata?.magicDescription ||
          table.dbtDescription ||
          table.comment,
        pinned: table.pinned,
        status:
          // Status is inherited from the schema/db if not set on the table
          table.metadata?.status ??
          table.dataSourceSchema.metadata?.status ??
          table.dataSourceSchema.dataSourceDatabase.metadata?.status ??
          null,
      }}
      showInDataBrowser={openDataBrowserOverride ?? showInDataBrowser}
    />
  );

  return (
    <MentionTooltip
      content={tooltip}
      disabled={!tooltip}
      maxWidthOverride={maxWidthOverride}
      placement={placement}
    >
      {children}
    </MentionTooltip>
  );
});
